import { Box, Button, Typography } from '@mui/material';
import ArticleSnippet from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/ArticleSnippet';
import formatMentions from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/formatMentions';
import Acknowledgement from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/Message/Acknowledgement';
import MessageBlocks from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/MessageBlocks';
import MessageReplied from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/MessageReplied';
import WhatsAppTextFormatter from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/WhatsAppTextFormatter';
import isEqual from 'lodash/isEqual';
import ForbidIcon from 'mdi-react/ForbidIcon';
import ShareIcon from 'mdi-react/ShareIcon';
import PropTypes, { bool, func, object, shape, string } from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import channelShape from 'shapes/channel';
import channels from 'store/app/entities/channels/action';
import { useLazyGetQuery } from 'store/app/entities/EmployeesApiSlice';
import authEmployeeSelector from 'store/selectors/authEmployee';
import extractMessagePreviewUrls from 'utils/extractMessagePreviewUrls';
import messagePreviewPollPlan, {
  PREVIEW_IMAGE_PENDING_RETRY_MS,
} from 'utils/messagePreviewPollPlan';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';
import AdminMenu from './AdminMenu';
import Attachments from './Attachments';
import connector from './connector';
import EmployeeLabel from './EmployeeLabel';
import LinkPreview from './LinkPreview';
import Message from './Message';
import MessageLabel from './MessageLabel';

const MessageGroup = ({
  actions,
  message: messageIn,
  channel,
  line = false,
  msgRef,
  isSelected,
  scrollToMessage,
  hideDate = false,
}) => {
  const [hover, setHoverValue] = useState(false);
  const [editing, setEditing] = useState(false);
  const previewPollRef = useRef({ id: messageIn._id, retried: false });
  const navigate = useNavigate();
  const [triggerGetEmployee] = useLazyGetQuery();
  const authEmployee = useSelector(authEmployeeSelector);
  const [channelSet] = useActions([channels.set]);

  const avatar = messageIn.employee?.avatar;

  const message = useMemo(
    () => ({
      ...messageIn,
      avatar: messageIn.sentAsChannel ? channel.avatar : avatar,
      name:
        messageIn.sentAsChannel && messageIn.employeeLabel ? channel.name : messageIn.employeeLabel,
      nameNotBlank: messageIn.sentAsChannel ? channel.name : messageIn.employeeName,
    }),
    [avatar, channel?.avatar, channel?.name, messageIn],
  );

  useEffect(() => {
    if (previewPollRef.current.id !== message._id) {
      previewPollRef.current = { id: message._id, retried: false };
    }
    const urls = extractMessagePreviewUrls(message.text).filter(url => url.type === 'url');
    if (!urls.length) return undefined;
    const plan = messagePreviewPollPlan({
      text: message.text,
      attachments: message.attachments,
      previews: message.previews,
      retried: previewPollRef.current.retried,
    });
    if (!plan.fetchNow && !plan.scheduleRetry) return undefined;

    const requestPreviews = () => {
      urls.forEach(url => {
        actions.messages.generatePreview(url.href, message._id, channel?._id);
      });
    };
    if (plan.fetchNow) {
      requestPreviews();
    }
    // Temp→server `_id` swap can drop the first fulfill; image ingest may land after
    // the first batch (`imagePending`). One follow-up is enough for the live card.
    if (!plan.scheduleRetry) return undefined;
    const retry = setTimeout(() => {
      previewPollRef.current.retried = true;
      requestPreviews();
    }, PREVIEW_IMAGE_PENDING_RETRY_MS);
    return () => clearTimeout(retry);
  }, [actions.messages, message, channel?._id]);

  const editClicked = () => {
    setEditing(true);
  };

  const editCanceled = () => {
    setEditing(false);
  };

  const deleteConfirmed = () => {
    actions.messages.destroy(message._id);
  };

  const canReply = channel?.isAdmin || !channel?.isReadOnly;

  const handleMessageDoubleClick = useCallback(
    event => {
      if (!canReply || message.temp || message.softDeletedAt) return;
      if (message.isMandatory || message.form) return;
      if (editing) return;
      if (
        event.target.closest(
          'a, button, [role="button"], input, textarea, select, video, audio, canvas, iframe',
        )
      ) {
        return;
      }
      event.preventDefault();
      actions.chatPanel.reply({ message, currentChannel: channel });
    },
    [actions.chatPanel, canReply, channel, editing, message],
  );

  const setHover = h => () => setHoverValue(h);

  const formatMentionNode = (txt, employeeId, userId, key) => {
    const openChannel = async () => {
      if (!userId) {
        // If no userId, do nothing
        return;
      }

      // Get employee data to create proper channel
      // employeeId might be an object, extract the _id
      const employeeIdString = typeof employeeId === 'object' ? employeeId._id : employeeId;
      triggerGetEmployee(employeeIdString)
        .unwrap()
        .then(async ({ data: { employee } }) => {
          if (employee.user?._id && employee.isAcceptTerms) {
            // Create channel object like DirectDrawer does
            const name = `${employee.firstName || ''} ${employee.surName || ''}`.trim();
            const directChannel = {
              _id: employee.user._id,
              name,
              isDirect: true,
              isActive: true,
              isReadOnly: false,
              isMuteable: false,
              avatar: employee.avatar,
              company_id: employee.company,
              complete: false,
              loadingMessages: false,
              messages_ids: [],
              page: 1,
              messagesUnreadCount: 0,
              messagesTotalCount: 0,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              lastMessage_id: null,
              isDeleted: false,
              employees_ids: authEmployee ? [employee._id, authEmployee._id] : [employee._id],
            };

            // Set the channel in the store and navigate
            await channelSet(directChannel);
            navigate(`/home/messages/${directChannel._id}`, { state: { channel: directChannel } });
          }
        })
        .catch(() => {
          // Fallback: navigate directly if employee fetch fails
          navigate(`/home/messages/${userId}`);
        });
    };
    return (
      <Button
        key={key}
        type="button"
        onClick={openChannel}
        sx={theme => ({
          padding: '0 2px',
          minHeight: 'auto',
          display: 'inline',
          borderRadius: '3px',
          color: theme.palette.mention,
          backgroundColor: theme.palette.mentionBack,
          border: 'none',
          fontSize: 'inherit',
          cursor: 'pointer',
          outline: 'none',
          textTransform: 'none',
          verticalAlign: 'baseline',
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: theme.palette.mentionBack,
          },
        })}
      >
        {txt}
      </Button>
    );
  };

  if (message?.isDeleted) {
    return null;
  }

  const isImage = message.attachments?.[0]?.mimeType?.toLowerCase()?.startsWith('image/');
  return (
    <Box
      ref={msgRef}
      sx={theme => ({
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        marginLeft: '-20px',
        marginRight: '-20px',
        paddingLeft: '20px',
        paddingRight: '20px',
        fontSize: '0.88235294rem',
        ...(isSelected && { backgroundColor: 'rgba(227, 228, 252, 0.5)' }),
      })}
    >
      {line ? (
        <Box
          sx={theme => ({
            borderBottomWidth: 1,
            color: theme.palette.primary.main,
            borderBottomColor: theme.palette.primary.main,
            marginTop: '15px',
            marginBottom: '10px',
            fontSize: '0.8125rem',
            textAlign: 'center',
            borderBottomStyle: 'solid',
          })}
        >
          {I18n.t('MessagesList.Unread')}
        </Box>
      ) : null}
      {/* Date separator is hoisted to MessagesList so it can be a sticky day-section header (OMM-603). */}
      <Box sx={{ marginBottom: '10px', marginTop: '15px' }}>
        <MessageLabel>{!hideDate && !message.temp ? message.dateTime : null}</MessageLabel>
      </Box>
      {!message.article && <EmployeeLabel right={message.isMine}>{message.name}</EmployeeLabel>}
      <Message message={message} channel={channel}>
        <Box
          sx={{ display: 'flex', flexDirection: 'row' }}
          onMouseEnter={setHover(true)}
          onMouseLeave={setHover(false)}
          onFocus={setHover(true)}
          onBlur={setHover(false)}
          onDoubleClick={handleMessageDoubleClick}
        >
          {}
          {message?.softDeletedAt ? (
            <Box
              sx={theme => ({
                fontSize: 16,
                color: theme.palette.text.primary,
                padding: '11.5px 30px 11.5px 15px',
                display: 'flex',
                alignItems: 'center',
                fontStyle: 'oblique',
              })}
            >
              <Box
                component="span"
                sx={{ marginRight: '4px', fontSize: 17, opacity: 0.6, display: 'flex' }}
              >
                <ForbidIcon />
              </Box>
              <div>{I18n.t('Message.deleted')}</div>
            </Box>
          ) : !message?.isMandatory && !message?.form ? (
            !editing && (
              <Box
                sx={{
                  ...(isImage && {
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: 'min-content',
                    minWidth: 100,
                    ...(message.text?.trim().replace('attachments', '') && { minWidth: 320 }),
                  }),
                }}
              >
                {!!message.showForwarded && (
                  <Box
                    sx={theme => ({
                      padding: '5px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      '& p': {
                        fontSize: 12,
                        color: theme.palette.secondary.main,
                      },
                    })}
                  >
                    <Box
                      component="span"
                      sx={{ color: 'secondary.main', fontSize: 20, display: 'flex', mr: 0.5 }}
                    >
                      <ShareIcon />
                    </Box>
                    <Typography>{I18n.t('Message.forwarded')}</Typography>
                  </Box>
                )}
                <MessageReplied message={message} scrollToMessage={scrollToMessage} />
                {Object.values(message.previews || {}).map((preview, index) => (
                  <LinkPreview key={index} preview={preview} messageId={message._id} />
                ))}
                <Attachments
                  attachments={message.attachments}
                  right={message.isMine}
                  messageId={message._id}
                  senderName={message.name}
                  senderAvatar={message.avatar}
                />
                {(message.blocks?.length || (message.text && message.text !== 'attachments')) && (
                  <Box
                    sx={theme => ({
                      color: theme.palette.text.primary,
                      padding: '11.5px 30px 11.5px 15px',
                      '& a': {
                        color: theme.palette.link.main,
                        '&:hover, &:active': {
                          color: theme.palette.link.hover,
                          textDecoration: 'underline',
                        },
                      },
                      '& p': {
                        marginBlockStart: 0,
                        '&:last-child': {
                          marginBlockEnd: 0,
                        },
                      },
                      '& hr': {
                        marginTop: theme.spacing(0.5),
                        marginBottom: theme.spacing(1),
                        border: 0,
                        width: '100%',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      },
                      '& blockquote': {
                        marginTop: theme.spacing(0.5),
                        marginBottom: theme.spacing(0.5),
                        paddingLeft: '16px',
                        position: 'relative',
                        marginBlockStart: 0,
                        marginBlockEnd: 0,
                        paddingBlockStart: theme.spacing(0.5),
                        paddingBlockEnd: theme.spacing(0.5),
                        marginInlineStart: 0,
                        marginInlineEnd: 0,
                        whiteSpace: 'normal',
                        '&:before': {
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: 0,
                          display: 'block',
                          width: theme.spacing(0.5),
                          borderRadius: theme.spacing(1),
                          background: theme.palette.secondary.light,
                          content: `''`,
                        },
                      },
                    })}
                  >
                    {!!message.text && (
                      <WhatsAppTextFormatter messageId={message._id}>
                        {formatMentions(message.text, message.mentions, formatMentionNode)}
                      </WhatsAppTextFormatter>
                    )}
                    <MessageBlocks message={message} />
                  </Box>
                )}
                <ArticleSnippet message={message} />
              </Box>
            )
          ) : (
            <Acknowledgement message={message} />
          )}
          <AdminMenu
            visible={hover}
            message={message}
            canReply={canReply}
            onEditClick={editClicked}
            onEditCanceled={editCanceled}
            onDeleteConfirmed={deleteConfirmed}
          />
        </Box>
      </Message>
    </Box>
  );
};

MessageGroup.propTypes = {
  msgRef: object.isRequired,
  isSelected: bool.isRequired,
  line: bool,
  actions: object.isRequired,
  channel: channelShape.isRequired,
  message: shape({
    text: string,
    seen: bool,
    isMandatory: bool,
    error: bool,
    loading: bool,
    employee: shape({
      name: string,
      avatar: string,
    }),
    dateTime: string,
    line: bool,
    reactions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        hasReacted: PropTypes.bool.isRequired,
      }),
    ),
  }).isRequired,
  scrollToMessage: func.isRequired,
  hideDate: bool,
};

const areEqual = (prevProps, nextProps) => {
  return (
    isEqual(prevProps.message, nextProps.message) &&
    prevProps.msgRef === nextProps.msgRef &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.hideDate === nextProps.hideDate &&
    isEqual(prevProps.channel, nextProps.channel)
  );
};

export default connector(memo(MessageGroup, areEqual));
